import type { ReactNode, ComponentProps } from 'react';
import classnames from 'classnames/bind';
import { isFunction, isNil } from 'lodash/fp';
import { Icon, IconSize, type IconVariant } from 'app/components/core/Icon';
import { useTheme } from 'app/components/core/ThemeProvider';
import { Button, ButtonSize, ButtonVariant } from 'app/components/core/Button';
import { useModalContext } from 'app/components/core/Modal/ModalContext';
import * as styles from './ModalHeader.css';

const cx = classnames.bind(styles);

export interface ModalHeaderProps extends ComponentProps<'header'> {
    testId?: string;
    className?: string;
    icon?: IconVariant;
    hideCloseButton?: boolean;
    children?: ReactNode;
}

export function ModalHeader({
    testId,
    className,
    icon,
    hideCloseButton = false,
    children,
    ...props
}: ModalHeaderProps) {
    const context = useModalContext();
    const { themeClassname } = useTheme();

    if (isNil(context)) {
        throw new Error('<ModalHeader> must be used within a <Modal> component.');
    }

    const { onClose, onHeaderPointerDown } = context;
    const classes = cx(
        'base',
        themeClassname,
        { 'is-movable': isFunction(onHeaderPointerDown) },
        className
    );

    return (
        <header
            data-testid={testId}
            className={classes}
            onPointerDown={onHeaderPointerDown}
            {...props}>
            {icon && <Icon className={cx('icon')} variant={icon} size={IconSize.SMALL} />}
            {children}
            {!hideCloseButton && (
                <Button
                    className={styles.close}
                    onClick={onClose}
                    label="Close"
                    icon="close"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.GHOST}
                />
            )}
        </header>
    );
}
